/**
 * Single source of truth for derived course status.
 *
 * The schema currently exposes three flags that can express the same idea
 * in different ways:
 *   - `is_published` (boolean)        → editorial visibility
 *   - `is_coming_soon` (boolean)      → marketing flag for upcoming courses
 *   - `syllabus.status` (string)      → legacy flag, still used in some rows
 *
 * To avoid scattered conditionals across the UI, derive a single status
 * from these flags using `getCourseStatus()`.
 *
 * NOTE: We intentionally do NOT change the underlying columns. The editor
 * still writes to the original toggles. This helper is read-only.
 */

export type CourseStatus = "draft" | "coming_soon" | "live";

export interface CourseStatusInput {
  is_published?: boolean | null;
  is_coming_soon?: boolean | null;
  syllabus?: unknown;
}

const isComingSoonSyllabus = (syllabus: unknown): boolean => {
  if (!syllabus || typeof syllabus !== "object") return false;
  return (syllabus as { status?: string }).status === "coming_soon";
};

export function getCourseStatus(course: CourseStatusInput): CourseStatus {
  // Unpublished always wins → draft (admins can preview separately)
  if (course.is_published === false) return "draft";

  // Coming soon: explicit flag OR legacy syllabus.status
  if (course.is_coming_soon === true || isComingSoonSyllabus(course.syllabus)) {
    return "coming_soon";
  }

  return "live";
}

/**
 * Returns true if the editor flags are in a contradictory state
 * (published + coming soon, or published flag mismatches syllabus).
 * Used to surface a warning in the admin editor.
 */
export function hasStatusConflict(course: CourseStatusInput): boolean {
  const syllabusComing = isComingSoonSyllabus(course.syllabus);
  const flagComing = course.is_coming_soon === true;
  // Conflict: one says coming soon, the other doesn't
  return syllabusComing !== flagComing;
}

/** Human label for badges. */
export function getCourseStatusLabel(status: CourseStatus): string {
  switch (status) {
    case "draft":
      return "Draft";
    case "coming_soon":
      return "Coming Soon";
    case "live":
      return "Live";
  }
}

/* ------------------------------------------------------------------ */
/*  Access expiration helpers                                          */
/* ------------------------------------------------------------------ */

export type AccessStatus = "lifetime" | "active" | "expiring_soon" | "grace" | "blocked";

export interface AccessStatusInput {
  /** ISO timestamp from enrollments.access_expires_at — null = lifetime */
  access_expires_at?: string | null;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const GRACE_DAYS = 2;

/**
 * Derive the user-facing access state for a course enrollment.
 * Backend webhook (`notify-expiring-access`) uses the same 2-day grace window.
 */
export function getAccessStatus(input: AccessStatusInput, now: Date = new Date()): AccessStatus {
  if (!input.access_expires_at) return "lifetime";
  const exp = new Date(input.access_expires_at).getTime();
  const diff = exp - now.getTime(); // positive = future
  if (diff > 3 * DAY_MS) return "active";
  if (diff > 0) return "expiring_soon";
  if (diff > -GRACE_DAYS * DAY_MS) return "grace";
  return "blocked";
}

export function getDaysRemaining(input: AccessStatusInput, now: Date = new Date()): number | null {
  if (!input.access_expires_at) return null;
  const exp = new Date(input.access_expires_at).getTime();
  return Math.ceil((exp - now.getTime()) / DAY_MS);
}

export function getAccessStatusLabel(status: AccessStatus): string {
  switch (status) {
    case "lifetime": return "Acesso vitalício";
    case "active": return "Ativo";
    case "expiring_soon": return "Expirando em breve";
    case "grace": return "Em cortesia (2 dias)";
    case "blocked": return "Acesso bloqueado";
  }
}

